import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { baseService } from './base.service'
import { CreateBlogDto, CreateBlogReactionDto, UpdateBlogDto } from './dto/blog-dto.interface'
import { IBaseService } from './interfaces/base-service.interface'

import { QueryParams } from '@/types/base.type'
import { BlogReaction, BlogType } from '@/types/blog.type'
import { CommentType } from '@/types/comment.type'
import { ListResponse } from '@/types/list.type'

export class BlogService {
  private static instance: BlogService
  private readonly api: AxiosInstance

  constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  private getQueryParams(locale?: string) {
    return locale ? `?locale=${locale}` : ''
  }

  public static getInstance(baseService: IBaseService): BlogService {
    if (!BlogService.instance) {
      BlogService.instance = new BlogService(baseService)
    }
    return BlogService.instance
  }

  getBlogs = async (
    {
      queryParams,
    }: {
      queryParams: QueryParams
    },
    locale?: string,
    configs?: AxiosRequestConfig
  ) => {
    const { pageNumber, pageSize, sort, search, searchFields } = queryParams
    let url = '/blog/blog-admin'

    if (pageNumber && pageSize) {
      url += `?pageNumber=${pageNumber}&pageSize=${pageSize}`
    }
    if (locale) {
      url += `&locale=${locale}`
    }
    if (sort) {
      url += `&sort=${sort}`
    }
    if (search) {
      url += `&search=${search}`
    }
    if (searchFields) {
      url += `&searchFields=${searchFields.join(',')}`
    }
    const { data } = await this.api.get<ListResponse<BlogType>>(url, configs)
    return data
  }

  getPublicBlogs = async (
    {
      pageNumber,
      pageSize,
      sort,
      locale,
      ...queryParams
    }: {
      pageNumber: number
      pageSize: number
      sort?: string
      locale?: string
    } & QueryParams,
    configs?: AxiosRequestConfig
  ) => {
    let url = `/blog/publish?pageNumber=${pageNumber}&pageSize=${pageSize}&locale=${locale}`
    if (sort) {
      url += `&sort=${sort}`
    }
    const { data } = await this.api.get<ListResponse<BlogType>>(url, {
      ...configs,
      params: {
        ...queryParams,
        ...configs?.params,
      },
    })
    return data
  }

  createBlog = async (blogDto: CreateBlogDto, locale?: string) => {
    const { data } = await this.api.post(`/blog/create${this.getQueryParams(locale)}`, blogDto)
    return data
  }

  getAdminBlogById = async <T>(id: number, locale?: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>(`/blog/${id}${this.getQueryParams(locale)}`, options)
    return data
  }

  deleteBlog = async (id: number, options?: AxiosRequestConfig) => {
    const { data } = await this.api.delete(`/blog/${id}`, options)
    return data
  }

  updateBlog = async (id: number, blogDto: UpdateBlogDto, locale?: string) => {
    const { data } = await this.api.put(`/blog/update/${id}${this.getQueryParams(locale)}`, blogDto)
    return data
  }

  getBlogBySlug = async <T = BlogType>(slug: string, locale?: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>(`/blog/slug/${slug}${this.getQueryParams(locale)}`, options)
    return data
  }

  getOwnerBlogs = async <T = ListResponse<BlogType>>(query: QueryParams, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>('/blog/author', {
      ...options,
      params: {
        ...query,
      },
    })
    return data
  }

  getReviewBlogs = async <T = ListResponse<BlogType>>(options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>('/blog/review', options)
    return data
  }

  getBlogReactions = async (id: number, config?: AxiosRequestConfig): Promise<ListResponse<BlogReaction>> => {
    const { data } = await this.api.get(`/blog/blog-reaction/${id}/reactions`, {
      ...config,
    })
    return data
  }

  toggleBlogReaction = async (id: number, reactionDto: CreateBlogReactionDto, config?: AxiosRequestConfig) => {
    const { data } = await this.api.post(`/blog/blog-reaction/${id}/react`, reactionDto, {
      ...config,
    })
    return data
  }

  removeBlogReaction = async (id: number, config?: AxiosRequestConfig) => {
    const { data } = await this.api.delete(`/blog/blog-reaction/${id}/react`, {
      ...config,
    })
    return data
  }

  getBlogComments = async (id: number, config?: AxiosRequestConfig) => {
    const { data } = await this.api.get<ListResponse<CommentType>>(`/blog/blog-comment/${id}/comments`, {
      ...config,
    })
    return data
  }
}

const blogService = BlogService.getInstance(baseService)

export default blogService
